// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits local SQLite via Tauri IPC (durable, offline, on-disk). Ported
// from the Flutter app's workouts/sets/exercises domain. Nothing here leaves the device.

import { invoke } from "./tauri-web";

export interface Workout {
  id: string;
  name: string;
  performed_on: string; // YYYY-MM-DD
  duration_minutes: number;
  notes: string | null;
  created_at: string;
  /** Downscaled JPEG data-URL of a photo attached to this workout (e.g. a form/progress shot),
   *  ON-DEVICE only. "" when unset. The model never sees it — a photo filed through chat is an attachment. */
  photo: string;
  total_sets: number;
  total_volume: number;
}

export interface WorkoutSet {
  id: string;
  workout_id: string;
  exercise_slug: string;
  exercise_name: string;
  set_index: number;
  weight: number;
  reps: number;
  /** RPE (Rate of Perceived Exertion, 6-10 in 0.5 steps). 0 = not recorded. */
  rpe: number;
  is_pr: boolean;
  pr_types: string[]; // e.g. ["heaviest","1RM"]
  notes: string | null;
  created_at: string;
}

export interface WorkoutDetail {
  workout: Workout;
  sets: WorkoutSet[];
}

export interface ExercisePR {
  exercise_slug: string;
  exercise_name: string;
  heaviest_weight: number;
  heaviest_weight_reps: number;
  estimated_one_rm: number;
  best_session_volume: number;
  performed_on: string;
}

/** One session's best set for an exercise (by est-1RM), for the progression chart. */
export interface SessionPoint {
  performed_on: string;
  top_weight: number;
  top_reps: number;
  est_one_rm: number;
}

export interface NewWorkout {
  name: string;
  performed_on: string;
  duration_minutes?: number;
  notes?: string | null;
}

export interface NewSet {
  workout_id: string;
  exercise_slug: string;
  exercise_name: string;
  weight: number;
  reps: number;
  /** Optional RPE (6-10, 0.5 steps). Omit or 0 = not recorded. */
  rpe?: number;
  notes?: string | null;
}

export interface RoutineExercise {
  slug: string;
  name: string;
}

/** One set from the previous session for an exercise — the "last time" anchor beside each set row. */
export interface PrevSet {
  weight: number;
  reps: number;
  rpe: number;
  set_index: number;
}

/** A reusable routine (template): a saved, ordered exercise list you repeat. */
export interface Routine {
  id: string;
  name: string;
  exercises: RoutineExercise[];
  created_at: string;
}

export interface NewRoutine {
  name: string;
  exercises: RoutineExercise[];
}

// ── AI-drafted training programs (PROGRAM-DESIGN.md) ──────────────────────────
export type TrainingGoal = "strength" | "hypertrophy" | "general_fitness";

export interface TrainingProfile {
  id: string;
  goal: TrainingGoal;
  days_per_week: number;
  avoid: string;
  updated_at: string;
}

export interface ProgramExercise {
  slug: string;
  name: string;
  target_sets: number;
  target_reps: number;
  target_weight: number;
}

export interface Program {
  id: string;
  name: string;
  goal: string;
  weeks_count: number;
  status: "active" | "completed" | "abandoned";
  created_at: string;
}

export interface ProgramSession {
  id: string;
  program_id: string;
  week_index: number;
  day_index: number;
  label: string;
  exercises: ProgramExercise[];
  completed_workout_id: string | null;
}

/** One drafted week, exactly as the AI returns it (before review/edit) or as sent to create_program. */
export interface DraftSession {
  day_index: number;
  label: string;
  exercises: ProgramExercise[];
}
export interface DraftWeek {
  week_index: number;
  sessions: DraftSession[];
}
export interface NewProgram {
  name: string;
  goal: string;
  weeks: DraftWeek[];
}

export const store = {
  listWorkouts: (limit?: number) => invoke<Workout[]>("list_workouts", { limit: limit ?? 50000 }),
  getWorkout: (id: string) => invoke<WorkoutDetail | null>("get_workout", { id }),
  createWorkout: (input: NewWorkout) => invoke<Workout>("create_workout", { input }),
  deleteWorkout: (id: string) => invoke<void>("delete_workout", { id }),
  /** Attach a photo (downscaled JPEG data-URL) to a workout — filed ON-DEVICE from chat (e.g. a form
   *  or progress shot). Never leaves the device, never sent to the model. "" clears it. */
  setWorkoutPhoto: (id: string, photo: string) => invoke<void>("set_workout_photo", { id, photo }),

  // reusable routines (templates)
  listRoutines: () => invoke<Routine[]>("list_routines"),
  createRoutine: (input: NewRoutine) => invoke<Routine>("create_routine", { input }),
  deleteRoutine: (id: string) => invoke<void>("delete_routine", { id }),

  // training profile + AI-drafted programs (PROGRAM-DESIGN.md)
  getTrainingProfile: () => invoke<TrainingProfile | null>("get_training_profile"),
  setTrainingProfile: (goal: TrainingGoal, days_per_week: number, avoid: string) =>
    invoke<TrainingProfile>("set_training_profile", { goal, days_per_week, avoid }),
  activeProgram: () => invoke<Program | null>("active_program"),
  listProgramSessions: (program_id: string) => invoke<ProgramSession[]>("list_program_sessions", { program_id }),
  nextProgramSession: () => invoke<ProgramSession | null>("next_program_session"),
  createProgram: (input: NewProgram) => invoke<Program>("create_program", { input }),
  abandonActiveProgram: () => invoke<void>("abandon_active_program"),
  /** Marks a planned session done and (native only) nudges that lift's future targets based on
   *  how this session actually went. Call right after logging the workout it corresponds to. */
  completeProgramSession: (session_id: string, workout_id: string) =>
    invoke<void>("complete_program_session", { session_id, workout_id }),

  // adds a set; returns the inserted set WITH server-computed PR flags
  addSet: (input: NewSet) => invoke<WorkoutSet>("add_set", { input }),
  deleteSet: (id: string) => invoke<void>("delete_set", { id }),

  // ── In-session (resumable) state + previous-session anchor ──
  /** The id of the current OPEN session (started, not finished), or null. Powers "resume today's session". */
  activeWorkoutId: () => invoke<string | null>("active_workout_id"),
  /** Mark a session finished (it stops being resumable and becomes a completed record). */
  finishWorkout: (id: string) => invoke<void>("finish_workout", { id }),
  /** The previous session's set list for an exercise (excluding the current workout) — the row anchors. */
  previousExerciseSets: (slug: string, excludeWorkoutId: string) =>
    invoke<PrevSet[]>("previous_exercise_sets", { slug, exclude_workout_id: excludeWorkoutId }),

  listPRs: () => invoke<ExercisePR[]>("list_prs"),
  exerciseHistory: (slug: string) => invoke<SessionPoint[]>("exercise_history", { slug }),

  // last-N-days workouts shaped for the AI brain: [{date, exercise, sets:[{weight,reps}]}]
  trendWorkouts: (days?: number) => invoke<TrendWorkoutRow[]>("trend_workouts", { days: days ?? 14 }),

  // DERIVED signals (small aggregates) computed locally in Rust — the richer AI payload
  trendSignals: (days?: number) => invoke<TrendSignals>("trend_signals", { days: days ?? 14 }),
};

// shape the Rust side emits for the brain payload — one row per (workout, exercise)
export interface TrendWorkoutRow {
  date: string;
  exercise: string;
  sets: { weight: number; reps: number }[];
}

// ── Derived training signals (mirror src-tauri/src/db.rs TrendSignals; snake_case) ──
// Small aggregates only — no raw set history. Optional/empty fields stay backward-
// tolerant: a signal that can't be derived (e.g. intensity needs logged durations)
// is simply absent.
export interface FrequencySignal {
  sessions_per_week: number;
  days_trained: number;
  rest_days: number;
}
export interface VolumeSignal {
  per_session: number[];
  slope_per_session: number;
  direction: "increasing" | "stable" | "decreasing";
  first_session: number;
  last_session: number;
  pct_change: number | null;
}
export interface IntensitySignal {
  per_session: number[];
  sessions_with_duration: number;
  first: number;
  last: number;
  avg: number;
  direction: "increasing" | "stable" | "decreasing";
}
export interface SplitSignal {
  session_groups: string[];
  distinct_groups: number;
  alternates: boolean;
  most_common_group: string | null;
  looks_like_ppl: boolean;
}
export interface SetFatigueSignal {
  exercises_measured: number;
  exercises_declining: number;
  avg_reps_drop: number;
  max_reps_drop: number;
  example_exercise: string | null;
}
export interface LiftSignal {
  exercise: string;
  sessions: number;
  first_top_set: number;
  last_top_set: number;
  best_est_1rm: number;
  direction: "increasing" | "stable" | "decreasing";
  prs: number;
  history: [string, number][]; // (date, top_weight) per session in the window, chronological — real chart data
}
// Pre-ranked, full-sentence observational fact built on-device (mirrors db.rs::HeadlineFact).
// Already contains the figures + unit context; tone lets the UI dot it without re-deriving.
export type FactTone = "good" | "watch" | "neutral";
export interface HeadlineFact {
  kind: "volume" | "frequency" | "lift" | "fatigue" | "intensity" | "split" | "pr" | "milestone";
  text: string;
  tone: FactTone;
}
export interface TrendSignals {
  window_days: number;
  workouts: number;
  total_sets: number;
  total_volume: number;
  date_first: string | null;
  date_last: string | null;
  frequency: FrequencySignal;
  volume: VolumeSignal;
  intensity: IntensitySignal | null;
  split: SplitSignal;
  set_fatigue: SetFatigueSignal;
  per_lift: LiftSignal[];
  prs_in_window: number;
  // Pre-ranked observational sentences (figures + tone) — the brain reasons over these,
  // and the UI shows them verbatim as "the numbers behind this".
  headline_facts: HeadlineFact[];
}

// ── Plate calculator (pure, on-device — Strong locks this behind Pro; ours is free) ──
// Standard plate inventories per side, in each unit. The bar is not included.
export const PLATES_KG = [25, 20, 15, 10, 5, 2.5, 1.25];
export const PLATES_LB = [45, 35, 25, 10, 5, 2.5];
export const DEFAULT_BAR_KG = 20;
export const DEFAULT_BAR_LB = 45;

export interface PlateResult {
  perSide: number[];      // plates to load on EACH side, largest first
  loadable: number;       // the weight actually achievable (bar + 2×plates)
  leftover: number;       // target - loadable (0 when exact)
  perSideWeight: number;  // total weight per side
}

/** Greedy plate breakdown for one side of the bar. Deterministic, no rounding surprises. */
export function plateBreakdown(target: number, bar: number, unit: "kg" | "lb"): PlateResult {
  const plates = unit === "kg" ? PLATES_KG : PLATES_LB;
  const perSideTarget = Math.max(0, (target - bar) / 2);
  const perSide: number[] = [];
  let remaining = perSideTarget;
  for (const p of plates) {
    while (remaining + 1e-9 >= p) { perSide.push(p); remaining -= p; }
  }
  const perSideWeight = perSide.reduce((s, x) => s + x, 0);
  const loadable = bar + perSideWeight * 2;
  return { perSide, loadable, leftover: Math.round((target - loadable) * 100) / 100, perSideWeight };
}
